import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { HomeIcon, SolutionsIcon, ContactIcon } from './NavIcons';
import { EditableContent } from '../EditableContent';

// Menu Icon for the bottom bar
const MenuIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
);

interface MobileBottomNavProps {
    onMenuClick: () => void;
    isRTL?: boolean;
}

export function MobileBottomNav({ onMenuClick, isRTL = false }: MobileBottomNavProps) {
    const pathname = usePathname();

    const navItems = [
        {
            key: 'nav.home',
            label: isRTL ? 'الرئيسية' : 'Home',
            icon: HomeIcon,
            href: '/',
            isActive: pathname === '/'
        },
        {
            key: 'nav.solutions',
            label: isRTL ? 'حلول' : 'Solutions',
            icon: SolutionsIcon,
            href: '/services/payment-solutions', // Direct link to a main solutions page or first item
            isActive: pathname?.includes('/services')
        },
        {
            key: 'nav.contact',
            label: isRTL ? 'تواصل' : 'Contact',
            icon: ContactIcon,
            href: '/contact',
            isActive: pathname === '/contact'
        },
        {
            key: 'nav.menu', // We'll add this key
            label: isRTL ? 'القائمة' : 'Menu',
            icon: MenuIcon,
            action: onMenuClick,
            isActive: false
        }
    ];

    return (
        <div className="fixed bottom-6 left-0 right-0 z-50 flex justify-center px-4 md:hidden pointer-events-none">
            <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, type: 'spring', stiffness: 260, damping: 20 }}
                className="pointer-events-auto flex items-center justify-around w-full max-w-sm bg-white/80 dark:bg-black/80 backdrop-blur-lg border border-white/20 dark:border-white/10 shadow-lg rounded-2xl px-2 py-3"
            >
                {navItems.map((item, index) => {
                    const Icon = item.icon;
                    const activeColor = "text-[#ED2245]";
                    const inactiveColor = "text-slate-500 dark:text-slate-400";

                    const content = (
                        <div className="flex flex-col items-center gap-1">
                            <div className={`relative p-1 rounded-xl transition-colors ${item.isActive ? 'bg-[#ED2245]/10' : ''}`}>
                                <Icon className={`w-6 h-6 ${item.isActive ? activeColor : inactiveColor}`} />
                                {item.isActive && (
                                    <motion.div
                                        layoutId="bottom-nav-indicator"
                                        className="absolute inset-0 rounded-xl bg-[#ED2245]/10"
                                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                    />
                                )}
                            </div>
                            <span className={`text-[10px] font-medium ${item.isActive ? activeColor : inactiveColor}`}>
                                <EditableContent id={item.key} showEditIcon={true} />
                            </span>
                        </div>
                    );

                    if (item.action) {
                        return (
                            <button
                                key={index}
                                onClick={item.action}
                                className="flex-1 flex justify-center focus:outline-none"
                                aria-label={item.label}
                            >
                                {content}
                            </button>
                        );
                    }

                    return (
                        <Link
                            key={index}
                            href={item.href || '#'}
                            prefetch={false}
                            className="flex-1 flex justify-center focus:outline-none"
                            aria-label={item.label}
                        >
                            {content}
                        </Link>
                    );
                })}
            </motion.div>
        </div>
    );
}
